import { useCallback, useEffect, useState } from "react";
import { Plus, ChevronLeft, ChevronRight, X, Search, TrendingUp, TrendingDown, Wallet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatTL, currentMonthKey, shiftMonth, monthLabel } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import TransactionModal from "../components/TransactionModal";
import TransactionItem from "../components/TransactionItem";
import { useAuth } from "../context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function TransactionsPage() {
  const { user } = useAuth();
  const perms = user?.permissions || {};
  const [month, setMonth] = useState(currentMonthKey());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState({ income: [], expense: [] });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const usingRange = !!(startDate || endDate);
  const PAGE_SIZE = 50;
  const [meta, setMeta] = useState({ total: 0, income: 0, expense: 0 });
  const [loadingMore, setLoadingMore] = useState(false);

  const buildUrl = useCallback((skip) => {
    let url = usingRange
      ? `/transactions?${startDate ? `start_date=${startDate}&` : ""}${endDate ? `end_date=${endDate}&` : ""}`.replace(/[&?]$/, "")
      : `/transactions?month=${month}`;
    if (!url.includes("?")) url += "?";
    if (typeFilter !== "all") url += `&type=${typeFilter}`;
    if (categoryFilter !== "all") url += `&category=${encodeURIComponent(categoryFilter)}`;
    if (q) url += `&q=${encodeURIComponent(q)}`;
    return `${url}&with_meta=1&limit=${PAGE_SIZE}&skip=${skip}`;
  }, [month, startDate, endDate, usingRange, typeFilter, categoryFilter, q]);

  const load = useCallback(async () => {
    const { data } = await api.get(buildUrl(0));
    setTransactions(data.items);
    setMeta({ total: data.total, income: data.income, expense: data.expense });
  }, [buildUrl]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get(buildUrl(transactions.length));
      setTransactions((prev) => [...prev, ...data.items]);
      setMeta({ total: data.total, income: data.income, expense: data.expense });
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { load(); }, [load]);
  const loadCategories = useCallback(() => api.get("/categories").then((r) => setCategories(r.data)), []);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const allCategories = typeFilter === "income" ? categories.income
    : typeFilter === "expense" ? categories.expense
    : [...categories.income, ...categories.expense];

  const totals = { income: meta.income, expense: meta.expense };
  const net = totals.income - totals.expense;

  const handleDelete = async () => {
    try {
      await api.delete(`/transactions/${deleting.id}`);
      toast.success("İşlem silindi");
      setDeleting(null);
      load();
    } catch {
      toast.error("İşlem silinemedi");
    }
  };

  return (
    <div className="space-y-6 fade-in-up" data-testid="transactions-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">İşlemler</h1>
        {perms.add && (
          <Button data-testid="add-transaction-button" onClick={() => { setEditing(null); setModalOpen(true); }} className="rounded-full font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Yeni İşlem
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input data-testid="transaction-search-input" placeholder="Açıklama veya kategori ara..."
            value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 rounded-full" />
          {searchInput && (
            <button data-testid="clear-search-button" onClick={() => setSearchInput("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className={`flex items-center bg-card border border-border rounded-full ${usingRange ? "opacity-40 pointer-events-none" : ""}`}>
          <button data-testid="month-prev-button" onClick={() => setMonth(shiftMonth(month, -1))}
            className="p-2 hover:text-primary transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <span data-testid="current-month-label" className="text-sm font-medium px-2 whitespace-nowrap">{monthLabel(month)}</span>
          <button data-testid="month-next-button" onClick={() => setMonth(shiftMonth(month, 1))}
            className="p-2 hover:text-primary transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>

        <div className="flex items-center gap-2">
          <Input data-testid="start-date-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="w-40 rounded-full" aria-label="Başlangıç tarihi" />
          <span className="text-muted-foreground text-sm">—</span>
          <Input data-testid="end-date-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="w-40 rounded-full" aria-label="Bitiş tarihi" />
          {usingRange && (
            <button data-testid="clear-date-range-button" onClick={() => { setStartDate(""); setEndDate(""); }}
              title="Tarih aralığını temizle"
              className="p-2 rounded-full bg-secondary text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setCategoryFilter("all"); }}>
          <SelectTrigger data-testid="type-filter-select" className="w-32 rounded-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="income">Gelir</SelectItem>
            <SelectItem value="expense">Gider</SelectItem>
          </SelectContent>
        </Select>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger data-testid="category-filter-select" className="w-44 rounded-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">Tüm Kategoriler</SelectItem>
            {allCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-3 gap-3" data-testid="filtered-summary-strip">
        <div className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3">
          <TrendingUp className="w-4 h-4 text-emerald-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Gelir</p>
            <p data-testid="filtered-income" className="text-sm md:text-base font-semibold text-emerald-400 truncate">{formatTL(totals.income)}</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3">
          <TrendingDown className="w-4 h-4 text-red-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Gider</p>
            <p data-testid="filtered-expense" className="text-sm md:text-base font-semibold text-red-400 truncate">{formatTL(totals.expense)}</p>
          </div>
        </div>
        <div className="bg-card border border-primary/40 rounded-xl px-4 py-3 flex items-center gap-3">
          <Wallet className="w-4 h-4 text-primary shrink-0" />
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Net Kar / Zarar</p>
            <p data-testid="filtered-net" className={`text-sm md:text-base font-semibold truncate ${net >= 0 ? "text-primary" : "text-red-400"}`}>{formatTL(net)}</p>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-4" data-testid="transactions-list">
        {transactions.length === 0 ? (
          <p data-testid="no-transactions-message" className="text-sm text-muted-foreground py-12 text-center">
            Filtrelere uygun işlem bulunamadı.
          </p>
        ) : (
          <>
            <div className="divide-y divide-border/50">
              {transactions.map((tx) => (
                <TransactionItem key={tx.id} tx={tx} meta={categories.meta}
                  onEdit={perms.edit ? (t) => { setEditing(t); setModalOpen(true); } : null}
                  onDelete={perms.delete ? setDeleting : null}
                  onViewReceipt={setViewingReceipt} />
              ))}
            </div>
            {transactions.length < meta.total && (
              <div className="pt-4 flex flex-col items-center gap-2">
                <p data-testid="pagination-info" className="text-xs text-muted-foreground">
                  {transactions.length} / {meta.total} işlem gösteriliyor
                </p>
                <Button data-testid="load-more-button" variant="outline" onClick={loadMore} disabled={loadingMore}
                  className="rounded-full border-border hover:bg-secondary">
                  {loadingMore && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Daha Fazla Yükle ({meta.total - transactions.length} kalan)
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <TransactionModal open={modalOpen} onOpenChange={setModalOpen} categories={categories} editing={editing} onSaved={load} onCategoriesChanged={loadCategories} />

      <Dialog open={!!viewingReceipt} onOpenChange={(o) => !o && setViewingReceipt(null)}>
        <DialogContent data-testid="receipt-view-dialog" className="bg-card border-border sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Fiş / Fatura</DialogTitle>
            <DialogDescription className="sr-only">İşleme eklenen fiş fotoğrafı</DialogDescription>
          </DialogHeader>
          {viewingReceipt && (
            <img src={`${API}/receipts/${viewingReceipt.receipt_id}`} alt="Fiş"
              data-testid="receipt-full-image" className="w-full rounded-xl border border-border" />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-card border-border" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">İşlemi sil?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. "{deleting?.category}" kaydı kalıcı olarak silinecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button" className="rounded-full">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button" onClick={handleDelete}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
